// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import { LuTriangleAlert, LuCircleAlert, LuCircleCheck, LuCircleSlash, LuClipboardCheck, LuBlocks } from 'react-icons/lu';

import { TestableCard, TestableText } from '@components/general/TestableComponents';
import TwoLineDonut from '@components/cards/shared/TwoLineDonut';

import { getRagMeta } from '../shared/businessViewConfig';

/** Icon per RAG status, so the status reads without relying on colour alone. */
const STATUS_ICONS = {
  at_risk: LuTriangleAlert,
  monitor: LuCircleAlert,
  healthy: LuCircleCheck,
  unavailable: LuCircleSlash,
};

/**
 * One of the two counts above the donut.
 * @param {{ testKey: string, label: string, value: React.ReactNode, icon: React.ElementType }} props
 */
const StatusCount = ({ testKey, label, value, icon: Icon }) => (
  <div className="flex-1 px-4 text-center">
    <TestableText dataTestId={`bv-flow-count-label-${testKey}`} className="text-gray-700 dark:text-gray-300">
      {label}
    </TestableText>
    <div className="mt-1 flex items-center justify-center gap-2">
      <Icon className="h-4 w-4 text-gray-500" aria-hidden="true" />
      <TestableText dataTestId={`bv-flow-count-${testKey}`} variant="h5" className="normal-case text-blue-500">
        {value}
      </TestableText>
    </div>
  </div>
);

StatusCount.propTypes = {
  testKey: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  icon: PropTypes.elementType.isRequired,
};

/**
 * Flow status card: the RAG verdict, how much was tested, and the split of the
 * linked test results as a pass-rate donut.
 *
 * The donut is built from the linked tests themselves, one segment per distinct
 * result, so a run that skipped tests is not drawn as though they failed.
 *
 * @param {Object} props
 * @param {import('../shared/businessView.types').BusinessViewWorkflowDetail} props.workflow
 * @param {import('../shared/businessView.types').FlowResultBreakdown[]} props.breakdown
 * @param {number} props.testCount        Number of linked tests actually returned.
 * @param {boolean} [props.isUnavailable] The workflow could not be calculated for this run.
 * @returns {JSX.Element}
 */
const BusinessViewFlowStatusCard = ({ workflow, breakdown = [], testCount, isUnavailable = false }) => {
  const { riskStatus, totalTests, affectedAreas, passRate, calculationMessage } = workflow || {};

  const status = riskStatus || 'unavailable';
  const { label, textClass, iconBorderClass } = getRagMeta(status);
  const StatusIcon = STATUS_ICONS[status] || STATUS_ICONS.unavailable;

  const hasChart = breakdown.length > 0;

  // p-0 because this card manages its own padding: the rules have to span its full width.
  return (
    <TestableCard dataTestId="bv-flow-status-card" className="flex h-full flex-col overflow-hidden p-0 shadow-sm">
      <div className="flex items-center gap-2 p-2">        
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md border ${iconBorderClass} ${textClass}`}>
          <StatusIcon size={24} aria-hidden="true" />
        </span>
        <TestableText
          dataTestId="bv-flow-status-label"
          variant="h4"
          className={`uppercase ${textClass}`}
        >
          {label}
        </TestableText>
      </div>

      {/*
        An unavailable calculation has no counts to show, so the reason the API gave
        replaces them rather than leaving three dashes to be interpreted.
      */}
      {isUnavailable && (
        <TestableText
          dataTestId="bv-flow-status-message"
          className="px-5 pb-5 text-gray-700 dark:text-gray-300"
        >
          {calculationMessage ||
            'This flow could not be calculated for this run, so it has no status or test counts.'}
        </TestableText>
      )}

      <hr className="border-gray-300 dark:border-gray-700" />

      <div className="flex divide-x divide-gray-300 py-2 dark:divide-gray-700">
        <StatusCount
          testKey="total-tests"
          label="Total tests"
          value={totalTests ?? testCount}
          icon={LuClipboardCheck}
        />
        <StatusCount
          testKey="affected-areas"
          label="Area Affected"
          value={affectedAreas ?? '—'}
          icon={LuBlocks}
        />
      </div>

      <hr className="border-gray-300 dark:border-gray-700" />

      {hasChart ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-2 sm:flex-row">
          <TwoLineDonut
            data={breakdown.map((entry) => ({ name: entry.label, count: entry.count }))}
            colours={breakdown.map((entry) => entry.chartColour)}
            topText={passRate == null ? '—' : `${Math.round(passRate)}%`}
            bottomText="Pass Rate"
            className="w-40 shrink-0"
          />

          <ul data-testid="bv-flow-result-legend" className="flex flex-col gap-2">
            {breakdown.map((entry) => (
              <li key={entry.result} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${entry.dotClass}`} />
                <span data-testid={`bv-flow-legend-${entry.result}`}>
                  {entry.count} {entry.label}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <TestableText
          dataTestId="bv-flow-no-results"
          className="flex-1 p-5 text-center text-gray-700 dark:text-gray-300"
        >
          No linked test results are available for this flow.
        </TestableText>
      )}
    </TestableCard>
  );
};

BusinessViewFlowStatusCard.propTypes = {
  workflow: PropTypes.shape({
    riskStatus: PropTypes.string,
    totalTests: PropTypes.number,
    affectedAreas: PropTypes.number,
    passRate: PropTypes.number,
    calculationMessage: PropTypes.string,
  }).isRequired,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({
      result: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      dotClass: PropTypes.string.isRequired,
      chartColour: PropTypes.string.isRequired,
    })
  ),
  testCount: PropTypes.number.isRequired,
  isUnavailable: PropTypes.bool,
};

export default BusinessViewFlowStatusCard;